import type {
  FptChangeClassification,
  FptEntitySyncMetadata,
  FptIdentityMapping,
  FptPipelineStepStatus,
  FptProjectLinkage,
  FptSyncStatus,
} from "../models/production";
import type { Asset, Person, Shot } from "../types";
import type { LegacyProjectRecord } from "../utils/fpt/projectRecordCompat";

export const FPT_SYNC_STATUS_LABELS: Record<FptSyncStatus, string> = {
  not_linked: "Not Linked",
  not_pushed: "Not Pushed",
  pending: "Pending",
  in_sync: "In Sync",
  out_of_sync: "Out of Sync",
  error: "Error",
};

export const FPT_PIPELINE_STEP_STATUS_LABELS: Record<FptPipelineStepStatus, string> = {
  valid: "Valid",
  missing: "Missing",
  unknown: "Unknown",
};

export const FPT_CHANGE_CLASSIFICATION_LABELS: Record<FptChangeClassification, string> = {
  new: "New",
  changed: "Changed",
  missing_in_fpt: "Missing in FPT",
  missing_in_app: "Missing in App",
  rebid: "Rebid",
  removed_from_current_bid: "Removed from Current Bid",
  archived_candidate: "Archived Candidate",
};

type SyncableBidEntity = Shot | Asset;

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableStringify(entry)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fpt_${(hash >>> 0).toString(16)}`;
}

function buildFingerprintPayload(entity: SyncableBidEntity, entityType: "shot" | "asset") {
  if (entityType === "shot") {
    const shot = entity as Shot;
    return {
      entityType,
      name: shot.shotCode,
      projectId: shot.projectId || null,
      seasonId: shot.seasonId || null,
      episodeId: shot.episodeId || null,
      sequenceId: shot.sequenceId || null,
      awardStatus: shot.awardStatus || null,
      progressStatus: shot.progressStatus || null,
      quantity: shot.quantity ?? null,
      days: shot.days || {},
      assumptions: shot.assumptions || "",
      scopeOfWork: shot.scopeOfWork || "",
      turnoverNotes: shot.turnoverNotes || "",
    };
  }

  const asset = entity as Asset;
  return {
    entityType,
    name: asset.assetCode,
    projectId: asset.projectId || null,
    seasonId: asset.seasonId || null,
    episodeId: asset.episodeId || null,
    sequenceId: asset.sequenceId || null,
    awardStatus: asset.awardStatus || null,
    progressStatus: asset.progressStatus || null,
    quantity: asset.quantity ?? null,
    type: asset.type || null,
    days: asset.days || {},
    assumptions: asset.assumptions || "",
    scopeOfWork: asset.scopeOfWork || "",
  };
}

export function buildSourceFingerprint(entity: SyncableBidEntity, entityType: "shot" | "asset"): string {
  return hashString(stableStringify(buildFingerprintPayload(entity, entityType)));
}

export function createEntitySyncMetadata(
  entity: SyncableBidEntity,
  entityType: "shot" | "asset",
  lastSyncedBidVersion?: string,
  existing?: Partial<FptEntitySyncMetadata>
): FptEntitySyncMetadata {
  const fptEntityId = entityType === "shot" ? (entity as Shot).fptShotId : (entity as Asset).fptAssetId;
  const sourceFingerprint = buildSourceFingerprint(entity, entityType);
  const taskIds = Array.isArray(entity.taskIds) ? entity.taskIds : [];
  const tasksMissingPipelineStep = existing?.tasksMissingPipelineStep;
  const derivedStatus: FptSyncStatus =
    existing?.syncStatus ||
    (fptEntityId ? (existing?.sourceFingerprint && existing.sourceFingerprint !== sourceFingerprint ? "out_of_sync" : "in_sync") : "not_pushed");

  return {
    appEntityId: entity.id,
    entityType,
    fptEntityId: fptEntityId || existing?.fptEntityId,
    fptEntityType: existing?.fptEntityType || (entityType === "shot" ? "Shot" : "Asset"),
    lastPushedAt: existing?.lastPushedAt || entity.lastFptSyncAt,
    lastPulledAt: existing?.lastPulledAt,
    lastSyncedBidVersion: existing?.lastSyncedBidVersion || lastSyncedBidVersion,
    syncStatus: derivedStatus,
    lastKnownFptStatus: existing?.lastKnownFptStatus || entity.progressStatus,
    pipelineStepStatus: existing?.pipelineStepStatus || "unknown",
    conflictFlag: existing?.conflictFlag || false,
    syncErrorMessage: existing?.syncErrorMessage || "",
    sourceFingerprint,
    changeClassification: existing?.changeClassification,
    removedFromCurrentBid: existing?.removedFromCurrentBid || false,
    archiveCandidate: existing?.archiveCandidate || false,
    fptTaskIds: existing?.fptTaskIds || [],
    taskCount: existing?.taskCount ?? taskIds.length,
    tasksMissingPipelineStep: tasksMissingPipelineStep ?? false,
    invoiceReady: existing?.invoiceReady ?? false,
    invoiceReadyReason: existing?.invoiceReadyReason || "",
    invoiceBlockedReason: existing?.invoiceBlockedReason || "",
    financials: existing?.financials,
    profitability: existing?.profitability,
  };
}

export function buildEntitySyncMetadataMap(
  shots: Shot[],
  assets: Asset[],
  lastSyncedBidVersion?: string,
  existingMap?: Record<string, FptEntitySyncMetadata>
): Record<string, FptEntitySyncMetadata> {
  const metadata: Record<string, FptEntitySyncMetadata> = {};

  for (const shot of shots) {
    metadata[shot.id] = createEntitySyncMetadata(shot, "shot", lastSyncedBidVersion, existingMap?.[shot.id]);
  }
  for (const asset of assets) {
    metadata[asset.id] = createEntitySyncMetadata(asset, "asset", lastSyncedBidVersion, existingMap?.[asset.id]);
  }

  return metadata;
}

export function normalizeProjectFptLinkage(projectRecord?: Partial<LegacyProjectRecord> | null): FptProjectLinkage {
  const rawProjectId = projectRecord?.fptProjectId;
  const fptProjectId =
    rawProjectId === undefined || rawProjectId === null
      ? undefined
      : String(rawProjectId);

  return {
    fptProjectId,
    fptProjectName: projectRecord?.fptProjectName,
    fptProjectUrl: projectRecord?.fptProjectUrl,
    fptLinked: projectRecord?.fptLinked ?? Boolean(fptProjectId),
    lastPushedAt: projectRecord?.lastPushedAt || projectRecord?.lastFptSyncAt,
    lastPulledAt: projectRecord?.lastPulledAt,
    lastSyncedBidVersion: projectRecord?.lastSyncedBidVersion,
    syncStatus: projectRecord?.syncStatus || (fptProjectId ? "not_pushed" : "not_linked"),
    financials: projectRecord?.financials,
    profitability: projectRecord?.profitability,
  };
}

export function createIdentityMapping(person: Person, existing?: Partial<FptIdentityMapping>): FptIdentityMapping {
  return {
    appUserId: person.id,
    email: person.email || person.googleWorkspaceEmail,
    fptUserId: person.fptPersonId || existing?.fptUserId,
    fptUserLinked: person.fptUserLinked ?? existing?.fptUserLinked ?? Boolean(person.fptPersonId),
    identitySource: person.identitySource || existing?.identitySource || "app",
    lastSyncedAt: person.lastSyncedAt || existing?.lastSyncedAt,
  };
}
